export type LogLevel =
  | "trace"
  | "debug"
  | "info"
  | "warn"
  | "error"
  | "fatal"
  | "silent";

function levelPriority(level: LogLevel): number {
  switch (level) {
    case "trace":
      return 10;
    case "debug":
      return 20;
    case "info":
      return 30;
    case "warn":
      return 40;
    case "error":
      return 50;
    case "fatal":
      return 60;
    case "silent":
      return 100;
  }
}

function stringifySafe(value: unknown): string {
  const seen = new WeakSet();
  try {
    return JSON.stringify(value, function (_k, v) {
      if (v && typeof v === "object") {
        if (seen.has(v)) return "[Circular]";
        seen.add(v);
      }
      if (typeof v === "object" && v instanceof Error) {
        const err = v as unknown as Error;
        return { name: err.name, message: err.message, stack: err.stack };
      }
      return v;
    });
  } catch {
    try {
      return String(value);
    } catch {
      return "[Unserializable]";
    }
  }
}

export class Logger {
  private name?: string;
  private level: LogLevel;
  private sendToSentry: boolean;

  constructor(options?: {
    name?: string;
    level?: LogLevel;
    sendToSentry?: boolean;
  }) {
    this.name = options?.name;
    this.level = options?.level ?? "info";
    this.sendToSentry = !!options?.sendToSentry;
  }

  setLevel(level: LogLevel) {
    this.level = level;
  }

  child(props?: { name?: string; level?: LogLevel; sendToSentry?: boolean }) {
    return new Logger({
      name: props?.name ?? this.name,
      level: props?.level ?? this.level,
      sendToSentry: props?.sendToSentry ?? this.sendToSentry,
    });
  }

  private shouldLog(level: LogLevel) {
    return levelPriority(level) >= levelPriority(this.level);
  }

  private format(level: LogLevel, msg: string, meta?: unknown) {
    const base: Record<string, unknown> = {
      time: new Date().toISOString(),
      level,
      name: this.name,
      msg,
    };
    if (meta !== undefined) {
      try {
        base["meta"] =
          typeof meta === "string" ? meta : JSON.parse(stringifySafe(meta));
      } catch {
        base["meta"] = stringifySafe(meta);
      }
    }
    return base;
  }

  private output(level: LogLevel, msg: string, meta?: unknown) {
    if (!this.shouldLog(level)) return;

    const payload = this.format(level, msg, meta);

    const isBrowser =
      typeof window !== "undefined" && typeof window.document !== "undefined";
    try {
      if (isBrowser) {
        const safeMeta =
          meta === undefined
            ? undefined
            : (() => {
                try {
                  return typeof meta === "string"
                    ? meta
                    : JSON.parse(stringifySafe(meta));
                } catch {
                  return stringifySafe(meta);
                }
              })();

        switch (level) {
          case "trace":
          case "debug":
            console.debug(payload.msg, safeMeta);
            break;
          case "info":
            console.info(payload.msg, safeMeta);
            break;
          case "warn":
            console.warn(payload.msg, safeMeta);
            break;
          case "error":
          case "fatal":
            try {
              console.error(payload.msg, safeMeta);
            } catch {
              /* swallow to avoid breaking iOS */
            }
            break;
        }
      } else {
        const out = JSON.stringify(payload);
        if (levelPriority(level) >= levelPriority("error")) {
          // eslint-disable-next-line no-console
          console.error(out);
        } else {
          // eslint-disable-next-line no-console
          console.log(out);
        }
      }
    } catch {
      // noop
    }

    if (this.sendToSentry && (level === "error" || level === "fatal")) {
      try {
        const Sentry = (globalThis as any).Sentry;
        if (Sentry && typeof Sentry.captureException === "function") {
          if (meta instanceof Error) {
            Sentry.captureException(meta);
          } else {
            Sentry.captureMessage(`${payload.msg} ${stringifySafe(meta)}`, {
              level: level === "fatal" ? "fatal" : "error",
            });
          }
        }
      } catch {
        // swallow Sentry errors
      }
    }
  }

  trace(msg: string, meta?: unknown) {
    this.output("trace", msg, meta);
  }
  debug(msg: string, meta?: unknown) {
    this.output("debug", msg, meta);
  }
  info(msg: string, meta?: unknown) {
    this.output("info", msg, meta);
  }
  warn(msg: string, meta?: unknown) {
    this.output("warn", msg, meta);
  }
  error(msg: string | Error, meta?: unknown) {
    if (msg instanceof Error) {
      this.output("error", msg.message, {
        ...(meta || {}),
        error: { name: msg.name, stack: msg.stack },
      });
    } else {
      this.output("error", msg, meta);
    }
  }
  fatal(msg: string | Error, meta?: unknown) {
    if (msg instanceof Error) {
      this.output("fatal", msg.message, {
        ...(meta || {}),
        error: { name: msg.name, stack: msg.stack },
      });
    } else {
      this.output("fatal", msg, meta);
    }
  }
}
